import { getZoomForLocationType } from '../search/utils';
import type { MapLocation, SearchMapCamera, SearchMapHandle } from './types';

export const LOCATION_MOVE_EVENT_DATA = { odSkipViewportSearch: true } as const;

/**
 * Moves the camera to a search destination without a viewport search of its
 * own; `onComplete` runs once the camera settles. Returns a cancel function.
 */
export const moveSearchCamera = ({
  map,
  camera,
  onComplete,
}: {
  map: SearchMapHandle;
  camera: SearchMapCamera & { zoom: number };
  onComplete: () => void;
}): (() => void) => {
  // Finish an earlier animation before subscribing to this selection's moveend.
  map.stop();
  let pending = true;
  const cancel = () => {
    if (!pending) {
      return;
    }
    pending = false;
    map.off('moveend', complete);
    map.stop();
  };
  const complete = () => {
    if (!pending) {
      return;
    }
    if (map.getBounds() === null) {
      map.once('moveend', complete);
      return;
    }
    pending = false;
    map.off('moveend', complete);
    onComplete();
  };
  map.once('moveend', complete);
  // A distant search is a new destination, not a tour across intervening cities.
  const withinView = map.getBounds()?.contains(camera.center);
  if (withinView) {
    map.flyTo({ ...camera, duration: 400 }, LOCATION_MOVE_EVENT_DATA);
  } else {
    map.jumpTo(camera, LOCATION_MOVE_EVENT_DATA);
  }
  // A repeated selection or reduced-motion preference can finish synchronously.
  if (!withinView || !map.isMoving()) {
    complete();
  }
  return cancel;
};

export const moveToSearchLocation = ({
  map,
  location,
  onComplete,
}: {
  map: SearchMapHandle;
  location: MapLocation & { coordinates: [number, number] };
  onComplete: () => void;
}): (() => void) => {
  if (location.bounds) {
    // A whole state shows at once.
    map.stop();
    map.fitBounds(location.bounds, { animate: false }, LOCATION_MOVE_EVENT_DATA);
    onComplete();
    return () => {};
  }
  return moveSearchCamera({
    map,
    camera: { center: location.coordinates, zoom: getZoomForLocationType(location.zoneType) },
    onComplete,
  });
};
